/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState, useEffect, useRef } from 'react';
import translate from 'services/i18n/translate';
import * as PinDuck from 'store/ducks/settings/managePin';
import { Ducks } from '@wit/vtv-x-foundation-lib';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { PrivateRoutePath } from 'routes/paths';
import Constants from 'configs/Constants';

interface Props {
  closeModal: () => void;
  onPinCallback?: (value?: string) => void;
  title?: string;
  validate?: boolean;
}
const Pin = ({ closeModal, onPinCallback, title, validate = true }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pin, setPin] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const validPin = pin.length === 4;
  const pinIsCorrect = useSelector(PinDuck.Selectors.getPinIsCorrect);
  const pinError = useSelector(PinDuck.Selectors.getError);
  const isLoadingUserPreferences = useSelector(Ducks.Household.Selectors.actions.updateUserPreference.isProcessing);
  const isValidatingPinSelector = useSelector(PinDuck.Selectors.isProcessing);
  const isLoading = isLoadingUserPreferences || isValidatingPinSelector;

  const focusBlockerClick = () => {
    inputRef.current?.focus();
  };
  const handleInput = (e: HTMLInputElement) => {
    setPin(e.value);
  };

  const handleConfirm = () => {
    if (validPin) {
      setShowForm(false);
      if (!isLoading) {
        if (validate) {
          dispatch(PinDuck.ActionCreators.pinValidate(pin));
        } else if (onPinCallback) {
          onPinCallback(pin);
        }
      }
    }
  };

  const goToHelp = () => {
    history.push(PrivateRoutePath.HELP);
  };

  useEffect(() => {
    if (pinError) {
      setShowError(true);
    }
  }, [pinError]);

  useEffect(() => {
    if (pinIsCorrect === undefined) {
      setShowError(false);
      setPin('');
      focusBlockerClick();
    } else if (pinIsCorrect === false) {
      setTimeout(() => {
        setShowError(true);
        setPin('');
      }, 500);
    } else if (pinIsCorrect === true) {
      if (validate && onPinCallback) {
        onPinCallback();
      } else {
        setTimeout(() => {
          setShowError(false);
          focusBlockerClick();
        }, 500);
      }
    }
    return () => {
      setPin('');
      setShowError(false);
      setShowForm(true);
    };
  }, [dispatch, onPinCallback, pinIsCorrect, validate]);

  useEffect(() => {
    if (!showError) {
      focusBlockerClick();
    }
  }, [showError]);

  const tryAgain = () => {
    setShowError(false);
    dispatch(PinDuck.ActionCreators.pinReset());
    setShowForm(true);
    setPin('');
  };

  return (
    <div className="modal-container">
      <div className="title-container">
        <span className="title" id="Pin_title">
          {showError ? translate['modal.title.error'] : title || translate.modalMasterPinTitle}
        </span>
      </div>
      <div className="message-container">
        <div id="Pin_messageContainer" className={`message ${showError ? 'error' : ''}`}>
          {showError ? (
            <div id="Pin_errorMessage">
              <p>{translate.error_WA750}</p>
              <a target="_blank" href={Constants.HELP_LINK} rel="noreferrer" id="Pin_errorHelpLink">
                {translate.modalMasterPinHiperlink}
              </a>
            </div>
          ) : (
            <>
              {isLoading ? <span className="spinner" /> : null}
              {!isLoading && !showError && showForm ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleConfirm();
                  }}
                >
                  <input
                    ref={inputRef}
                    id="Pin_inputText"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    maxLength={4}
                    onChange={(e) => handleInput(e.target)}
                  />
                  <div className="visible-pin-container">
                    <span>{pin.length > 0 ? '●' : '-'}</span>
                    <span>{pin.length > 1 ? '●' : '-'}</span>
                    <span>{pin.length > 2 ? '●' : '-'}</span>
                    <span>{pin.length > 3 ? '●' : '-'}</span>
                  </div>
                </form>
              ) : null}

              <div onClick={focusBlockerClick} className="focus-blocker" />
            </>
          )}
        </div>
      </div>
      <div className="buttons-container" id="Modal_buttonsContainer">
        {showError ? (
          <>
            <a className="button" onClick={tryAgain} id="Pin_button___tryAgain">
              <span className="button-text">{translate['modal.button.tryAgain']}</span>
            </a>
            <a className="button" onClick={goToHelp} id="Pin_button___help">
              <span className="button-text">{translate['modal.button.help']}</span>
            </a>
          </>
        ) : (
          <>
            <a
              className={`button ${isLoading || !validPin ? 'disabled' : ''}`}
              id="Pin_button___confirm"
              onClick={handleConfirm}
            >
              <span className="button-text">{translate.modalButtonOk}</span>
            </a>
            <a className={`button ${isLoading ? 'disabled' : ''}`} onClick={closeModal} id="Pin_button___cancel">
              <span className="button-text">{translate.modalButtonCancel}</span>
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(Pin);
